import React, { useEffect, useState } from "react";
import Layout from "../layout/Layout";
import FlightIcon from "../assets/images/flight.png";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getbookingdetail } from "../Redux/Action/BookingAction";
import moment from "moment";
import { Accordion } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import FirstLetterCapital from "../CustomHooks/FirstLetterCapital";
import BackButton from "../components/ui/BackButton";
import Api from "../Config/ApiConfig";
import { toast } from "react-toastify";
import defaultAvatar from "../assets/images/default-avatar.png";

function PlanTypePill({ type }) {
  const colors = {
    premium: "linear-gradient(90deg, #2B8B6F, #17C470)",
    medium: "linear-gradient(90deg, #034328, #2B8B6F)",
    basic: "linear-gradient(90deg, #FFD700, #FFA500)",
  };
  return (
    <span
      style={{
        padding: "4px 12px",
        borderRadius: 16,
        fontSize: "0.8rem",
        fontWeight: 600,
        color: "#fff",
        background: colors[type] || "#b0b0b0",
        whiteSpace: "nowrap",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 1,
      }}
    >
      {type ? type.charAt(0).toUpperCase() + type.slice(1) : "N/A"}
    </span>
  );
}

// Utility function to capitalize and format status/stage strings
function formatStatus(str) {
  if (!str) return 'N/A';
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export default function BookingDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formatCurrency = (n) => (typeof n === "number" ? n.toLocaleString("en-US", { style: "currency", currency: "USD" }) : "N/A");

  useEffect(() => {
    dispatch(getbookingdetail(id));
  }, [id, dispatch]);
  const bookinginfo = useSelector((state) => state.Booking.bookingdetails);
  const trailJourneyStatus = bookinginfo?.trailDetails?.journey_status;
  const journeyTimelineSteps = [];
  const [imagePreview, setImagePreview] = useState(null);

  // Flight editing & tickets states
  const [showEditFlight, setShowEditFlight] = useState(false);
  const [flightForm, setFlightForm] = useState({
    flight_number: '',
    departure_date: '',
    departure_time: '',
    departure_airport: '',
    destination_airport: '',
    departure_airport_name: '',
    destination_airport_name: '',
    airline_name: '',
    is_manual_flight: false
  });
  const [submittingFlight, setSubmittingFlight] = useState(false);

  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);

  const [supportChatSession, setSupportChatSession] = useState(null);
  const [supportChatMessages, setSupportChatMessages] = useState([]);
  const [loadingSupportChat, setLoadingSupportChat] = useState(false);
  const [chatReplyText, setChatReplyText] = useState('');
  const [sendingChatReply, setSendingChatReply] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(true);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [submittingCancel, setSubmittingCancel] = useState(false);

  const fetchTickets = async () => {
    try {
      setLoadingTickets(true);
      const res = await Api.get(`/booking/${id}/tickets`);
      const ticketsList = Array.isArray(res.data?.data)
        ? res.data.data
        : (Array.isArray(res.data) ? res.data : []);
      setTickets(ticketsList);
    } catch (err) {
      console.error("Failed to load tickets:", err);
    } finally {
      setLoadingTickets(false);
    }
  };

  const fetchSupportChat = async () => {
    try {
      setLoadingSupportChat(true);
      const res = await Api.get(`/booking/${id}/support-chat`);
      if (res.data?.success) {
        setSupportChatSession(res.data.data?.session || null);
        setSupportChatMessages(res.data.data?.messages || []);
      }
    } catch (err) {
      console.error("Failed to load support chat:", err);
    } finally {
      setLoadingSupportChat(false);
    }
  };

  const handleSendBookingChatReply = async (e) => {
    if (e) e.preventDefault();
    if (!chatReplyText.trim() || sendingChatReply) return;

    try {
      setSendingChatReply(true);
      const res = await Api.post(`/booking/${id}/support-chat/reply`, {
        message: chatReplyText.trim(),
        session_id: supportChatSession?.session_id
      });
      if (res.data?.success) {
        toast.success("Reply sent to customer successfully");
        setSupportChatMessages(prev => [...prev, res.data.data]);
        setChatReplyText('');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send reply");
    } finally {
      setSendingChatReply(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    fetchSupportChat();
  }, [id]);

  const handleOpenEditFlight = () => {
    const fd = bookinginfo?.flight_details || {};
    let depDateStr = '';
    if (fd.departure_date) {
      depDateStr = moment(fd.departure_date).format('YYYY-MM-DD');
    }
    let depTimeStr = '';
    if (fd.departure_time) {
      depTimeStr = moment(fd.departure_time).format('HH:mm');
    }
    setFlightForm({
      flight_number: fd.flight_number || '',
      departure_date: depDateStr,
      departure_time: depTimeStr,
      departure_airport: fd.departure_airport || '',
      destination_airport: fd.destination_airport || '',
      departure_airport_name: fd.departure_airport_name || '',
      destination_airport_name: fd.destination_airport_name || '',
      airline_name: fd.airline_name || '',
      is_manual_flight: Boolean(fd.is_manual_flight)
    });
    setShowEditFlight(true);
  };

  const handleSaveFlight = async (e) => {
    e.preventDefault();
    try {
      setSubmittingFlight(true);
      await Api.put(`/booking/${id}/flight-details`, {
        flight_details: {
          ...flightForm,
          departure_time: flightForm.departure_time ? `${flightForm.departure_date}T${flightForm.departure_time}:00.000Z` : undefined
        }
      });
      toast.success("Flight details updated successfully");
      setShowEditFlight(false);
      dispatch(getbookingdetail(id));
      fetchTickets();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setSubmittingFlight(false);
    }
  };

  const handleUpdateTicketStatus = async (ticketId, status) => {
    try {
      await Api.put(`/tickets/${ticketId}/status`, {
        status,
        admin_notes: `Status updated to ${status} by admin`
      });
      toast.success(`Ticket marked as ${status}`);
      fetchTickets();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    }
  };

  const handleAdminCancelAndRefund = async () => {
    try {
      setSubmittingCancel(true);
      await Api.post('/booking/cancel-refund', {
        booking_id: id,
        reason: cancelReason || "Admin cancellation and refund"
      });
      toast.success("Booking cancelled and refund initiated successfully");
      setShowCancelModal(false);
      dispatch(getbookingdetail(id));
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setSubmittingCancel(false);
    }
  };

  if (trailJourneyStatus) {
    const orderedSteps = [
      ["pickup_user", "pickup_user"],
      ["pickup_user", "entry_to_airport"],
      ["pickup_user", "check_in_airline_counter"],
      ["pickup_user", "security_check"],
      ["pickup_user", "immigration_check"],
      ["in_transit", "reaching_boarding_gate"],
      ["in_transit", "boarding"],
      ["in_transit", "in_flight"],
      ["arrival", "landed"],
      ["arrival", "baggage_claim"],
      ["arrival", "customs"],
      ["arrival", "exit"]
    ];

    orderedSteps.forEach(([stageKey, statusKey]) => {
      const node = trailJourneyStatus?.[stageKey]?.[statusKey];
      if (!node || (!node.status && !node.date)) return;
      journeyTimelineSteps.push({
        id: `${stageKey}.${statusKey}`,
        label: formatStatus(statusKey),
        stage: formatStatus(stageKey),
        date: node.date,
        picture: node.picture
      });
    });
  }
  return (
    <Layout>
      {/* Breadcrumb */}
      <div style={{ 
        padding: '12px 20px', 
        backgroundColor: '#f8f9fa', 
        borderBottom: '1px solid #dee2e6',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <BackButton />
          <nav style={{ fontSize: '14px', color: '#6c757d' }}>
            <span>
              <a href="/dashboard" style={{ color: '#007bff', textDecoration: 'none' }}>Dashboard</a>
              {' > '}
              <a href="/booking-request-from-care-seeker" style={{ color: '#007bff', textDecoration: 'none' }}>Booking Requests</a>
              {' > '}
              <span style={{ color: '#6c757d' }}>
                Booking #{bookinginfo?.booking_number || 'Details'}
              </span>
            </span>
          </nav>
        </div>
      </div>

      <div style={{ padding: '20px', backgroundColor: '#f5f5f5', minHeight: '500px' }}>
        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%', marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e9ecef' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#333' }}>
                Booking ID: <span style={{ color: '#2B8B6F', fontWeight: 700 }}>{bookinginfo?.booking_number}</span>
                    </div>
                    <span style={{ 
                      marginLeft: 8,
                      padding: '4px 12px',
                      borderRadius: '16px',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: '#fff',
                      backgroundColor: bookinginfo?.booking_status === 'booking_request_initiated' ? '#6c757d' : '#28a745',
                      whiteSpace: 'nowrap'
                    }}>
                      {formatStatus(bookinginfo?.booking_status)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <a
                      href={bookinginfo?.care_seeker ? `/users/${typeof bookinginfo.care_seeker === 'object' ? bookinginfo.care_seeker.$oid || bookinginfo.care_seeker._id : bookinginfo.care_seeker}` : undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="common-btn"
                      style={{ padding: '8px 12px' }}
                    >
                      View Care Seeker
                    </a>
                    <button
                      className="common-btn"
                      style={{ padding: '8px 12px' }}
                      onClick={() => navigator.clipboard?.writeText(bookinginfo?.booking_number || '')}
                    >
                      Copy Booking #
                    </button>
                  </div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px', marginTop: '20px' }}>
                <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                  <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '12px', fontSize: '1.1rem', borderBottom: '2px solid #2B8B6F', paddingBottom: '8px' }}>Booking Summary</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'center' }}>
                    <span><b>Booking Number:</b> {bookinginfo?.booking_number}</span>
                    <span style={{ 
                      padding: '4px 12px',
                      borderRadius: '16px',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: '#fff',
                      backgroundColor: bookinginfo?.booking_status === 'booking_request_initiated' ? '#6c757d' : '#28a745',
                      whiteSpace: 'nowrap'
                    }}>{formatStatus(bookinginfo?.booking_status)}</span>
                    <span><b>Plan Type:</b> <PlanTypePill type={bookinginfo?.plan_type} /></span>
                  </div>
                  <div><b>Created At:</b> {bookinginfo?.createdAt ? moment(bookinginfo?.createdAt).format("DD MMM YYYY h:mm A") : 'N/A'}</div>
                  <div><b>Updated At:</b> {bookinginfo?.updatedAt ? moment(bookinginfo?.updatedAt).format("DD MMM YYYY h:mm A") : 'N/A'}</div>
                  <div><b>Booking For:</b> {bookinginfo?.booking_for || 'N/A'}</div>
                  <div><b>Live Location Enabled:</b> {bookinginfo?.live_location_enabled ? "Yes" : "No"}</div>
                  <div><b>Share Flight Details:</b> {bookinginfo?.share_flight_details ? "Yes" : "No"}</div>
                  <div><b>OTP Verified:</b> {bookinginfo?.is_otp_verified ? "Yes" : "No"}</div>
                  <div><b>Chat/Call Enabled:</b> {bookinginfo?.chat_call ? "Yes" : "No"}</div>
                </div>
                <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                  <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '12px', fontSize: '1.1rem', borderBottom: '2px solid #2B8B6F', paddingBottom: '8px' }}>Care Seeker</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                    <a
                      href={bookinginfo?.care_seeker ? `/users/${typeof bookinginfo.care_seeker === 'object' ? bookinginfo.care_seeker.$oid || bookinginfo.care_seeker._id : bookinginfo.care_seeker}` : undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    >
                      <div style={{ width: 64, height: 64, borderRadius: '50%', overflow: 'hidden', border: '2px solid #e9ecef' }}>
                        <img
                          src={bookinginfo?.care_seeker_details?.profile_picture || defaultAvatar}
                          alt="profile"
                          onError={(e) => { e.currentTarget.src = defaultAvatar; }}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#034328' }}>{bookinginfo?.care_seeker_details?.name || 'N/A'}</div>
                        <div style={{ color: '#2B8B6F', fontSize: '1rem' }}>Age: {bookinginfo?.care_seeker_details?.age || 'N/A'}</div>
                        <div style={{ color: '#2B8B6F', fontSize: '1rem' }}>Gender: {FirstLetterCapital(bookinginfo?.care_seeker_details?.gender) || 'N/A'}</div>
                        <div style={{ color: '#2B8B6F', fontSize: '1rem' }}>Languages: {(bookinginfo?.care_seeker_details?.language_preference || []).join(', ') || 'N/A'}</div>
                      </div>
                    </a>
                  </div>
                </div>
                {bookinginfo?.carer && (
                  <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                    <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '12px', fontSize: '1.1rem', borderBottom: '2px solid #2B8B6F', paddingBottom: '8px' }}>Assigned Carer</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                      <a
                        href={`/users/${typeof bookinginfo.carer === 'object' ? bookinginfo.carer.$oid || bookinginfo.carer._id : bookinginfo.carer}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                      >
                        <div style={{ width: 64, height: 64, borderRadius: '50%', overflow: 'hidden', border: '2px solid #e9ecef' }}>
                          <img
                            src={bookinginfo?.carer_details?.profile_picture || defaultAvatar}
                            alt="profile"
                            onError={(e) => { e.currentTarget.src = defaultAvatar; }}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#034328' }}>{bookinginfo?.carer_details?.name || 'N/A'}</div>
                          <div style={{ color: '#2B8B6F', fontSize: '1rem' }}>Gender: {FirstLetterCapital(bookinginfo?.carer_details?.gender) || 'N/A'}</div>
                        </div>
                      </a>
                    </div>
                  </div>
                )}
                <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                  <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '12px', fontSize: '1.1rem', borderBottom: '2px solid #2B8B6F', paddingBottom: '8px' }}>Passengers</div>
                  {(bookinginfo?.passengers || []).length === 0 ? <div>N/A</div> : (
                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                      {bookinginfo?.passengers?.map((p, idx) => (
                        <div key={p._id?.$oid || idx} style={{ padding: '12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e9ecef', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <img
                              src={p.profile_picture || defaultAvatar}
                              alt="profile"
                              onError={(e) => { e.currentTarget.src = defaultAvatar; }}
                              style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover' }}
                            />
                            <div>
                              <div style={{ fontWeight: 600 }}>{p.name || 'N/A'}</div>
                              <div style={{ color: '#2B8B6F', fontSize: '0.98rem' }}>Gender: {FirstLetterCapital(p.gender) || 'N/A'}</div>
                              <div style={{ color: '#2B8B6F', fontSize: '0.98rem' }}>Age: {p.age || 'N/A'}</div>
                              <div style={{ color: '#2B8B6F', fontSize: '0.98rem' }}>Languages: {(p.language || []).join(', ') || 'N/A'}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef', gridColumn: '1 / -1' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', borderBottom: '2px solid #2B8B6F', paddingBottom: '8px', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontWeight: 'bold', color: '#333', fontSize: '1.1rem' }}>Flight Details</span>
                      {bookinginfo?.flight_details?.is_manual_flight && (
                        <span style={{ backgroundColor: '#FEF3C7', color: '#92400E', border: '1px solid #F59E0B', borderRadius: 6, padding: '2px 8px', fontSize: '0.8rem', fontWeight: 600 }}>
                          ⚠️ Local / Unlisted Flight (Manual Entry)
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{ backgroundColor: '#2B8B6F', color: '#fff', borderRadius: 6, fontWeight: 600, padding: '5px 12px' }}
                        onClick={handleOpenEditFlight}
                      >
                        ✏️ Edit Flight Details
                      </button>
                      {!['cancelled', 'completed', 'cancellation_completed'].includes(bookinginfo?.booking_status) && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          style={{ borderRadius: 6, fontWeight: 600, padding: '5px 12px' }}
                          onClick={() => setShowCancelModal(true)}
                        >
                          Cancel & Refund
                        </button>
                      )}
                    </div>
                  </div>
                  {bookinginfo?.flight_details?.is_manual_flight && (
                    <div style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '8px 12px', marginBottom: 14, color: '#92400E', fontSize: '0.88rem' }}>
                      <b>Warning:</b> This flight was not found in the live online flight tracker. Flight route and date were entered manually.
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <div style={{ fontWeight: 600, color: '#034328', fontSize: '1.1rem' }}>{bookinginfo?.flight_details?.departure_airport}</div>
                      <div style={{ color: '#888', fontSize: '0.98rem' }}>{bookinginfo?.flight_details?.departure_airport_name}</div>
                      <div style={{ color: '#888', fontSize: '0.98rem' }}>{bookinginfo?.flight_details?.departure_city}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}>
                      <img src={FlightIcon} style={{ width: 38, height: 38 }} alt="Flight Icon" />
                    </div>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <div style={{ fontWeight: 600, color: '#034328', fontSize: '1.1rem' }}>{bookinginfo?.flight_details?.destination_airport}</div>
                      <div style={{ color: '#888', fontSize: '0.98rem' }}>{bookinginfo?.flight_details?.destination_airport_name}</div>
                      <div style={{ color: '#888', fontSize: '0.98rem' }}>{bookinginfo?.flight_details?.destination_city}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <div style={{ color: '#2B8B6F', fontWeight: 600, fontSize: '1.08rem' }}>
                        {bookinginfo?.flight_details?.departure_date ? moment(bookinginfo?.flight_details?.departure_date).format("DD MMM YYYY") : 'N/A'}, {bookinginfo?.flight_details?.departure_time ? moment(bookinginfo?.flight_details?.departure_time).format("h:mm A") : 'N/A'}
                      </div>
                      <div style={{ color: '#888', fontSize: '0.98rem' }}>Flight No: <span style={{ color: '#034328', fontWeight: 600 }}>{bookinginfo?.flight_details?.flight_number || 'N/A'}</span></div>
                      <div style={{ color: '#888', fontSize: '0.98rem' }}>Airline: <span style={{ color: '#034328', fontWeight: 600 }}>{bookinginfo?.flight_details?.airline_name || 'N/A'}</span></div>
                    </div>
                  </div>
                  {bookinginfo?.flight_details?.layovers && bookinginfo?.flight_details?.layovers.length > 0 && (
                    <div style={{ marginTop: 8, color: '#888' }}><b>Layovers:</b> {bookinginfo?.flight_details?.layovers.join(', ')}</div>
                  )}
                </div>

                {/* Support Tickets Section */}
                <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef', gridColumn: '1 / -1' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', borderBottom: '2px solid #2B8B6F', paddingBottom: '8px', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ fontWeight: 'bold', color: '#333', fontSize: '1.1rem' }}>
                      Support Tickets ({tickets.length})
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={fetchTickets}
                      disabled={loadingTickets}
                    >
                      {loadingTickets ? 'Refreshing...' : '🔄 Refresh Tickets'}
                    </button>
                  </div>
                  {tickets.length === 0 ? (
                    <div style={{ color: '#888', fontStyle: 'italic', padding: '10px 0' }}>
                      No support tickets raised for this booking.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {tickets.map((tkt) => (
                        <div
                          key={tkt._id}
                          style={{
                            padding: '12px 16px',
                            backgroundColor: '#fff',
                            borderRadius: 8,
                            border: '1px solid #ddd',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                            <div>
                              <span style={{ fontWeight: 700, color: '#034328', marginRight: 8 }}>{tkt.ticket_number}</span>
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: 4,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                backgroundColor: tkt.status === 'resolved' ? '#DEF7EC' : (tkt.status === 'rejected' ? '#FDE8E8' : '#FEF08A'),
                                color: tkt.status === 'resolved' ? '#03543F' : (tkt.status === 'rejected' ? '#9B1C1C' : '#713F12')
                              }}>
                                {tkt.status}
                              </span>
                              <span style={{ marginLeft: 8, color: '#666', fontSize: '0.85rem' }}>
                                Type: <b>{tkt.type?.replace('_', ' ')}</b> • Raised by: <b>{tkt.user_id ? `${tkt.user_id.first_name} ${tkt.user_id.last_name} (${tkt.user_role})` : tkt.user_role}</b>
                              </span>
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#888' }}>
                              {moment(tkt.createdAt).format('DD MMM YYYY h:mm A')}
                            </div>
                          </div>

                          <div style={{ marginTop: 8, fontSize: '0.92rem', color: '#333' }}>
                            <b>Reason:</b> {tkt.reason}
                          </div>

                          {tkt.requested_changes && (tkt.requested_changes.flight_number || tkt.requested_changes.departure_date) && (
                            <div style={{ marginTop: 6, padding: '8px 12px', backgroundColor: '#F9FAFB', borderRadius: 6, border: '1px dashed #CBD5E1', fontSize: '0.88rem' }}>
                              <b>Requested Changes:</b>
                              {tkt.requested_changes.flight_number && <span style={{ marginLeft: 8 }}>Flight: <b>{tkt.requested_changes.flight_number}</b></span>}
                              {tkt.requested_changes.departure_date && <span style={{ marginLeft: 8 }}>Date: <b>{moment(tkt.requested_changes.departure_date).format('DD MMM YYYY')}</b></span>}
                              {tkt.requested_changes.departure_airport && <span style={{ marginLeft: 8 }}>From: <b>{tkt.requested_changes.departure_airport}</b></span>}
                              {tkt.requested_changes.destination_airport && <span style={{ marginLeft: 8 }}>To: <b>{tkt.requested_changes.destination_airport}</b></span>}
                              {tkt.requested_changes.additional_notes && <div style={{ marginTop: 4, color: '#64748B' }}>Notes: {tkt.requested_changes.additional_notes}</div>}
                            </div>
                          )}

                          {tkt.status === 'pending' && (
                            <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                              <button
                                type="button"
                                className="btn btn-sm btn-success"
                                style={{ fontSize: '0.8rem', padding: '3px 10px' }}
                                onClick={() => handleUpdateTicketStatus(tkt._id, 'resolved')}
                              >
                                ✓ Mark Resolved
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                style={{ fontSize: '0.8rem', padding: '3px 10px' }}
                                onClick={() => handleUpdateTicketStatus(tkt._id, 'rejected')}
                              >
                                ✗ Reject
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Customer Support Chat & Direct Admin Reply */}
                <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef', gridColumn: '1 / -1' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', borderBottom: '2px solid #2B8B6F', paddingBottom: '8px', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ fontWeight: 'bold', color: '#333', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>💬 Customer Support Chat</span>
                      <span style={{ fontSize: '0.8rem', backgroundColor: '#E0F2FE', color: '#0369A1', padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>
                        {supportChatMessages.length} {supportChatMessages.length === 1 ? 'message' : 'messages'}
                      </span>
                      {supportChatSession?.session_id && (
                        <span style={{ fontSize: '0.75rem', color: '#64748B', fontFamily: 'monospace' }}>
                          ({supportChatSession.session_id})
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={fetchSupportChat}
                        disabled={loadingSupportChat}
                      >
                        {loadingSupportChat ? 'Refreshing...' : '🔄 Refresh Chat'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setIsChatOpen(!isChatOpen)}
                      >
                        {isChatOpen ? 'Hide Chat' : 'Show Chat'}
                      </button>
                    </div>
                  </div>

                  {isChatOpen && (
                    <div>
                      {/* Chat Messages Container */}
                      <div
                        style={{
                          maxHeight: '380px',
                          overflowY: 'auto',
                          padding: '14px',
                          backgroundColor: '#f0f2f5',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 10,
                          marginBottom: '14px'
                        }}
                      >
                        {supportChatMessages.length === 0 ? (
                          <div style={{ color: '#888', fontStyle: 'italic', textAlign: 'center', padding: '20px 0' }}>
                            No support chat messages exchanged yet for this booking. You can initiate a reply below.
                          </div>
                        ) : (
                          supportChatMessages.map((msg, idx) => {
                            const isUser = msg.role === 'user';
                            const isAdminReply = msg.metadata?.is_admin_reply;

                            return (
                              <div
                                key={msg._id || idx}
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: isUser ? 'flex-end' : 'flex-start'
                                }}
                              >
                                <div
                                  style={{
                                    maxWidth: '80%',
                                    padding: '10px 14px',
                                    borderRadius: '12px',
                                    borderTopRightRadius: isUser ? '2px' : '12px',
                                    borderTopLeftRadius: !isUser ? '2px' : '12px',
                                    backgroundColor: isUser
                                      ? '#2B8B6F'
                                      : (isAdminReply ? '#EFF6FF' : '#FFFFFF'),
                                    color: isUser ? '#FFFFFF' : '#1E293B',
                                    border: isUser
                                      ? 'none'
                                      : (isAdminReply ? '1px solid #BFDBFE' : '1px solid #E2E8F0'),
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                  }}
                                >
                                  {!isUser && (
                                    <div style={{ fontSize: '0.72rem', fontWeight: 700, marginBottom: 4, color: isAdminReply ? '#2563EB' : '#059669', display: 'flex', alignItems: 'center', gap: 4 }}>
                                      <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: isAdminReply ? '#2563EB' : '#059669', display: 'inline-block' }}></span>
                                      <span>{isAdminReply ? (msg.metadata?.admin_name ? `Support (${msg.metadata.admin_name})` : 'Support Team') : 'AI Assistant'}</span>
                                    </div>
                                  )}
                                  <div style={{ fontSize: '0.9rem', lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
                                    {msg.content}
                                  </div>
                                  <div style={{ fontSize: '0.7rem', color: isUser ? 'rgba(255,255,255,0.8)' : '#94A3B8', marginTop: 4, textAlign: 'right' }}>
                                    {moment(msg.createdAt).format('hh:mm A')}
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>

                      {/* Reply Input Bar */}
                      <form onSubmit={handleSendBookingChatReply} style={{ display: 'flex', gap: 8 }}>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Type an official reply to customer..."
                          value={chatReplyText}
                          onChange={(e) => setChatReplyText(e.target.value)}
                          disabled={sendingChatReply}
                          style={{ fontSize: '0.9rem', borderRadius: 6 }}
                        />
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={sendingChatReply || !chatReplyText.trim()}
                          style={{ minWidth: '130px', fontSize: '0.88rem', fontWeight: 600, backgroundColor: '#2B8B6F', borderColor: '#2B8B6F' }}
                        >
                          {sendingChatReply ? 'Sending...' : 'Send Reply ✈️'}
                        </button>
                      </form>
                    </div>
                  )}
                </div>
                <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                  <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '12px', fontSize: '1.1rem', borderBottom: '2px solid #2B8B6F', paddingBottom: '8px' }}>Preferences</div>
                  <div><b>Gender:</b> {FirstLetterCapital(bookinginfo?.preferences?.gender) || 'N/A'}</div>
                  <div><b>Languages:</b> {(bookinginfo?.preferences?.language || []).map(l => l.name + (l.proficiency ? ` (${l.proficiency})` : '')).join(', ') || 'N/A'}</div>
                  <div><b>Medical Background:</b> {bookinginfo?.preferences?.medical_background === false ? 'No' : 'Yes'}</div>
                </div>
                <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                  <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '12px', fontSize: '1.1rem', borderBottom: '2px solid #2B8B6F', paddingBottom: '8px' }}>Payment</div>
                  {(() => {
                    const paymentSubtotal = Number(bookinginfo?.payment_details?.subtotal || 0);
                    const promoDiscountRate = Number(bookinginfo?.payment_details?.discount_rate || 0);
                    const promoDiscountType = bookinginfo?.payment_details?.discount_type || 'percentage';
                    const paymentTotalAmt = Number(bookinginfo?.payment_details?.total_amount || 0);

                    // If promo code has percentage rate, display nominal discount (e.g. 99% of $244 = $241.56)
                    let paymentDiscount = Number(bookinginfo?.payment_details?.discount || 0);
                    if (promoDiscountType === 'percentage' && promoDiscountRate > 0 && paymentSubtotal > 0) {
                      paymentDiscount = Math.round((paymentSubtotal * (promoDiscountRate / 100)) * 100) / 100;
                    }

                    // Calculate low order fee floor adjustment
                    let paymentLowOrderFee = Number(bookinginfo?.payment_details?.low_order_fee || 0);
                    const afterDiscount = Math.max(0, paymentSubtotal - paymentDiscount);
                    if (paymentTotalAmt > afterDiscount && paymentLowOrderFee === 0) {
                      paymentLowOrderFee = Math.round((paymentTotalAmt - afterDiscount) * 100) / 100;
                    }

                    return (
                      <>
                        <div><b>Subtotal:</b> {formatCurrency(paymentSubtotal)}</div>
                        {paymentDiscount > 0 && (
                          <div><b>Discount:</b> -{formatCurrency(paymentDiscount)}</div>
                        )}
                        {bookinginfo?.payment_details?.promo_code && (
                          <div><b>Promo Code:</b> <span style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold' }}>{bookinginfo.payment_details.promo_code}</span> {promoDiscountRate ? `(${promoDiscountRate}% ${promoDiscountType || 'discount'})` : ''}</div>
                        )}
                        {paymentLowOrderFee > 0 && (
                          <div style={{ color: '#0d6efd' }}><b>Low Order Fee / Min Fare Floor:</b> +{formatCurrency(paymentLowOrderFee)}</div>
                        )}
                        {Number(bookinginfo?.payment_details?.platform_booking_charge || 0) > 0 && (
                          <div><b>Platform Booking Charge:</b> {formatCurrency(bookinginfo?.payment_details?.platform_booking_charge)}</div>
                        )}
                        {Number(bookinginfo?.payment_details?.wallet_deduction || 0) > 0 && (
                          <div><b>Wallet Deduction:</b> {formatCurrency(bookinginfo?.payment_details?.wallet_deduction)}</div>
                        )}
                        <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px dashed #ccc' }}><b>Total Amount (Card Charged):</b> <span style={{ fontWeight: 'bold', color: '#2B8B6F' }}>{formatCurrency(paymentTotalAmt)}</span></div>
                        <div><b>Platform Commission (10% of Charged):</b> {formatCurrency(Math.round(paymentTotalAmt * 0.10 * 100) / 100)}</div>
                        <div><b>Carer Payout (90% of Charged):</b> {formatCurrency(Math.round(paymentTotalAmt * 0.90 * 100) / 100)}</div>
                        <div><b>Payment Method:</b> {bookinginfo?.payment_method?.wallet || bookinginfo?.payment_method?.type || 'N/A'}</div>
                        <div><b>Payment Status:</b> {formatStatus(bookinginfo?.payment_details?.payment_status || bookinginfo?.payment_status) || 'N/A'}</div>
                      </>
                    );
                  })()}
                </div>
                <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                  <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '12px', fontSize: '1.1rem', borderBottom: '2px solid #2B8B6F', paddingBottom: '8px' }}>Review</div>
                  <div><b>Rating:</b> {bookinginfo?.review?.rating != null ? bookinginfo?.review?.rating : 'N/A'}</div>
                  <div><b>Feedback:</b> {bookinginfo?.review?.feedback || 'N/A'}</div>
                </div>
                <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                  <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '12px', fontSize: '1.1rem', borderBottom: '2px solid #2B8B6F', paddingBottom: '8px' }}>Journey Status</div>
                  <div><b>Stage:</b> {formatStatus(bookinginfo?.journey_status?.stage) || 'N/A'}</div>
                  <div><b>Status:</b> {formatStatus(bookinginfo?.journey_status?.status) || 'N/A'}</div>
                </div>
                {journeyTimelineSteps.length > 0 && (
                  <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef', gridColumn: '1 / -1' }}>
                    <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '12px', fontSize: '1.1rem', borderBottom: '2px solid #2B8B6F', paddingBottom: '8px' }}>Journey Status Timeline</div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, width: '100%' }}>
                      <div style={{ position: 'relative', paddingLeft: 10, flex: 1 }}>
                        <div style={{ position: 'absolute', top: 4, bottom: 4, left: 4, width: 2, background: '#e0e0e0' }} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                          {journeyTimelineSteps.map((step, idx) => (
                            <div
                              key={step.id}
                              style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}
                            >
                              <div style={{ position: 'relative', zIndex: 1, marginTop: 2 }}>
                                <div
                                  style={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    backgroundColor: '#2B8B6F',
                                    border: '2px solid #fff',
                                    boxShadow: '0 0 0 2px rgba(43,139,111,0.2)'
                                  }}
                                />
                              </div>
                              <div style={{ flex: 1, minWidth: 0, borderRadius: 6, padding: '6px 10px', backgroundColor: idx === journeyTimelineSteps.length - 1 ? '#F0FBF7' : '#fafafa', border: '1px solid #eee' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                                  <div>
                                    <div style={{ fontWeight: 600, color: '#034328', fontSize: '0.9rem' }}>{step.label}</div>
                                    <div style={{ fontSize: '0.8rem', color: '#666' }}>{step.stage}</div>
                                  </div>
                                  {step.date && (
                                    <div style={{ fontSize: '0.78rem', color: '#888', whiteSpace: 'nowrap' }}>
                                      {moment(step.date).format("DD MMM YYYY h:mm A")}
                                    </div>
                                  )}
                                </div>
                                {step.picture && (
                                  <div style={{ marginTop: 6 }}>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setImagePreview({
                                          url: step.picture,
                                          label: step.label,
                                          stage: step.stage,
                                          date: step.date
                                        })
                                      }
                                      style={{
                                        display: 'inline-flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textDecoration: 'none',
                                        color: '#2B8B6F',
                                        background: 'transparent',
                                        border: 'none',
                                        padding: 0,
                                        cursor: 'pointer'
                                      }}
                                    >
                                      <img
                                        src={step.picture}
                                        alt="Journey"
                                        style={{
                                          width: 56,
                                          height: 56,
                                          objectFit: 'cover',
                                          borderRadius: 6,
                                          border: '1px solid #ddd',
                                          boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                                          transition: 'transform 0.2s ease-out, box-shadow 0.2s ease-out'
                                        }}
                                      />
                                      <span style={{ fontSize: '0.75rem', marginTop: 2 }}>Photo</span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                    </div>
                  </div>
                </div>
              )}
                {imagePreview && (
                  <Modal
                    show={!!imagePreview}
                    onHide={() => setImagePreview(null)}
                    centered
                    size="lg"
                  >
                  >
                    <Modal.Header closeButton>
                      <Modal.Title>
                        {imagePreview.label || 'Journey Photo'}
                        {imagePreview.stage && (
                          <span style={{ marginLeft: 8, fontSize: '0.85rem', color: '#6b7280' }}>
                            {imagePreview.stage}
                          </span>
                        )}
                      </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 12
                        }}
                      >
                        {imagePreview.date && (
                          <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                            {moment(imagePreview.date).format("DD MMM YYYY h:mm A")}
                          </div>
                        )}
                        <div
                          style={{
                            borderRadius: 12,
                            overflow: 'hidden',
                            boxShadow: '0 20px 45px rgba(0,0,0,0.35)',
                            maxHeight: '70vh',
                            background: '#000'
                          }}
                        >
                          <img
                            src={imagePreview.url}
                            alt="Journey"
                            style={{
                              display: 'block',
                              maxWidth: '100%',
                              maxHeight: '70vh',
                              objectFit: 'contain',
                              transform: 'scale(1.01)',
                              transition: 'transform 0.35s ease-out'
                            }}
                          />
                        </div>
                        <a
                          href={imagePreview.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: '0.85rem', color: '#2B8B6F', marginTop: 4 }}
                        >
                          Open original in new tab
                        </a>
                      </div>
                    </Modal.Body>
                  </Modal>
                )}
                {Array.isArray(bookinginfo?.trailDetails) && bookinginfo.trailDetails.length > 0 && (
                  <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef', gridColumn: '1 / -1' }}>
                    <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '12px', fontSize: '1.1rem', borderBottom: '2px solid #2B8B6F', paddingBottom: '8px' }}>Trail</div>
                    <Accordion defaultActiveKey="0">
                      {bookinginfo.trailDetails.map((t, idx) => (
                        <Accordion.Item eventKey={String(idx)} key={t._id?.$oid || idx}>
                          <Accordion.Header>
                            {(t?.status || '').toString().replace(/_/g, ' ').toUpperCase()} — {t?.createdAt ? moment(t.createdAt).format('DD MMM YYYY h:mm A') : 'N/A'}
                          </Accordion.Header>
                          <Accordion.Body>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                              <div><b>Booking Number:</b> {t?.booking_number || bookinginfo?.booking_number || 'N/A'}</div>
                              <div><b>Notes:</b> {t?.note || 'N/A'}</div>
                              <div><b>By:</b> {(t?.updated_by || t?.created_by || 'N/A').toString()}</div>
                              <div><b>ID:</b> {typeof t?._id === 'object' ? (t?._id?.$oid || 'N/A') : (t?._id || 'N/A')}</div>
                            </div>
                          </Accordion.Body>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  </div>
                )}
                <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                  <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '12px', fontSize: '1.1rem', borderBottom: '2px solid #2B8B6F', paddingBottom: '8px' }}>IDs (Admin/Debug)</div>
                  <div><b>Booking ID:</b> {bookinginfo?._id?._id || bookinginfo?._id?.$oid || bookinginfo?._id || 'N/A'}</div>
                  <div><b>Care Seeker ID:</b> {bookinginfo?.care_seeker?._id || bookinginfo?.care_seeker?.$oid || bookinginfo?.care_seeker || 'N/A'}</div>
                  <div><b>OTP ID:</b> {bookinginfo?.otp_id?._id || bookinginfo?.otp_id?.$oid || bookinginfo?.otp_id || 'N/A'}</div>
                  <div><b>Trail ID:</b> {bookinginfo?.trail_id?._id || bookinginfo?.trail_id?.$oid || bookinginfo?.trail_id || 'N/A'}</div>
                  <div><b>Payment Intent:</b> {bookinginfo?.paymentintent || 'N/A'}</div>
                </div>
                <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef', gridColumn: '1 / -1' }}>
                  <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '12px', fontSize: '1.1rem', borderBottom: '2px solid #2B8B6F', paddingBottom: '8px' }}>Checklist Proofs</div>
                  {(!bookinginfo?.checklist || bookinginfo.checklist.length === 0) ? (
                    <div style={{ color: '#888' }}>No checklist data available.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      {bookinginfo.checklist.slice().sort((a, b) => a.stageOrder - b.stageOrder).map((stage, sIdx) => (
                        <div key={sIdx} style={{ border: '1px solid #eee', borderRadius: 8, padding: 12 }}>
                          <div style={{ fontWeight: 600, color: '#034328', marginBottom: 8, fontSize: '1.05rem', borderBottom: '1px solid #eee', paddingBottom: 4 }}>
                            {stage.stageName}
                          </div>
                          <div style={{ display: 'grid', gap: 12 }}>
                            {stage.items.slice().sort((a, b) => a.order - b.order).map((item, iIdx) => (
                              <div key={iIdx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '4px 0', borderTop: iIdx > 0 ? '1px dashed #eee' : 'none' }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: 500 }}>{item.title}</div>
                                  {item.description && <div style={{ fontSize: '0.9rem', color: '#666' }}>{item.description}</div>}
                                  <div style={{ fontSize: '0.85rem', marginTop: 4 }}>
                                    <span style={{ 
                                      padding: '2px 8px', 
                                      borderRadius: 4, 
                                      backgroundColor: item.status === 'completed' ? '#E8F9F2' : item.status === 'skipped' ? '#FFF9E7' : '#f5f5f5',
                                      color: item.status === 'completed' ? '#2B8B6F' : item.status === 'skipped' ? '#FFA500' : '#888',
                                      fontWeight: 600
                                    }}>
                                      {item.status.toUpperCase()}
                                    </span>
                                    {item.completedAt && <span style={{ color: '#888', marginLeft: 8 }}>{moment(item.completedAt).format("DD MMM h:mm A")}</span>}
                                    {item.flightType !== 'both' && <span style={{ marginLeft: 8, color: '#666', fontSize: '0.8rem', border: '1px solid #ddd', padding: '0 4px', borderRadius: 4 }}>{item.flightType}</span>}
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                  {item.proof?.photoUrl && (
                                    <a href={item.proof.photoUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textDecoration: 'none', color: '#2B8B6F' }}>
                                      <img src={item.proof.photoUrl} alt="Proof" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }} />
                                      <span style={{ fontSize: '0.75rem', marginTop: 2 }}>Photo</span>
                                    </a>
                                  )}
                                  {item.proof?.location?.lat && (
                                    <a 
                                      href={`https://www.google.com/maps/search/?api=1&query=${item.proof.location.lat},${item.proof.location.lng}`} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textDecoration: 'none', color: '#2B8B6F' }}
                                    >
                                      <div style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#E8F9F2', borderRadius: 4 }}>
                                        📍
                                      </div>
                                      <span style={{ fontSize: '0.75rem', marginTop: 2 }}>Location</span>
                                    </a>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
            </div>
          </div>

        {/* Modal: Edit Flight Details */}
        <Modal show={showEditFlight} onHide={() => setShowEditFlight(false)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Edit Flight Details</Modal.Title>
          </Modal.Header>
          <form onSubmit={handleSaveFlight}>
            <Modal.Body>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-bold">Flight Number</label>
                  <input
                    type="text"
                    className="form-control"
                    value={flightForm.flight_number}
                    onChange={(e) => setFlightForm({ ...flightForm, flight_number: e.target.value.toUpperCase() })}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Airline Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={flightForm.airline_name}
                    onChange={(e) => setFlightForm({ ...flightForm, airline_name: e.target.value })}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Departure Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={flightForm.departure_date}
                    onChange={(e) => setFlightForm({ ...flightForm, departure_date: e.target.value })}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Departure Time (HH:mm)</label>
                  <input
                    type="time"
                    className="form-control"
                    value={flightForm.departure_time}
                    onChange={(e) => setFlightForm({ ...flightForm, departure_time: e.target.value })}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Departure Airport Code</label>
                  <input
                    type="text"
                    className="form-control"
                    value={flightForm.departure_airport}
                    onChange={(e) => setFlightForm({ ...flightForm, departure_airport: e.target.value.toUpperCase() })}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Departure Airport Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={flightForm.departure_airport_name}
                    onChange={(e) => setFlightForm({ ...flightForm, departure_airport_name: e.target.value })}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Destination Airport Code</label>
                  <input
                    type="text"
                    className="form-control"
                    value={flightForm.destination_airport}
                    onChange={(e) => setFlightForm({ ...flightForm, destination_airport: e.target.value.toUpperCase() })}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold">Destination Airport Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={flightForm.destination_airport_name}
                    onChange={(e) => setFlightForm({ ...flightForm, destination_airport_name: e.target.value })}
                  />
                </div>
                <div className="col-12">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isManualFlightCheck"
                      checked={flightForm.is_manual_flight}
                      onChange={(e) => setFlightForm({ ...flightForm, is_manual_flight: e.target.checked })}
                    />
                    <label className="form-check-label fw-bold" htmlFor="isManualFlightCheck">
                      Mark as Local / Unlisted Flight (Manual Entry)
                    </label>
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button type="button" className="btn btn-secondary" onClick={() => setShowEditFlight(false)}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn"
                style={{ backgroundColor: '#2B8B6F', color: '#fff', fontWeight: 600 }}
                disabled={submittingFlight}
              >
                {submittingFlight ? 'Saving...' : 'Save Flight Details'}
              </button>
            </Modal.Footer>
          </form>
        </Modal>

        {/* Modal: Admin Cancel & Refund */}
        <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Cancel Booking & Initiate Refund</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-danger fw-bold">
              Are you sure you want to cancel this booking and initiate a full refund to the user?
            </p>
            <div className="mb-3">
              <label className="form-label fw-bold">Reason for Cancellation</label>
              <textarea
                className="form-control"
                rows="3"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter cancellation reason..."
                required
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-secondary" onClick={() => setShowCancelModal(false)}>
              Close
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleAdminCancelAndRefund}
              disabled={submittingCancel || !cancelReason.trim()}
            >
              {submittingCancel ? 'Processing...' : 'Confirm Cancel & Refund'}
            </button>
          </Modal.Footer>
        </Modal>
      </Layout>
  );
}
